/**
 * Progressive Image Component
 * Provides smooth image loading with:
 * - Blur-to-clear transition for uncached images
 * - Direct display for cached images (no transition)
 * - Client-side caching
 * - Error handling with fallback
 */

import React, { useState, useEffect, useRef } from 'react';

// Utility function to combine class names
function cn(...classes: (string | undefined | false | null)[]) {
  return classes.filter(Boolean).join(' ');
}

interface ProgressiveImageProps {
  src: string;
  alt: string;
  className?: string;
  placeholderClassName?: string;
  placeholderColor?: string;
  children?: React.ReactNode; // Fallback content
  onLoad?: () => void;
  onError?: () => void;
  style?: React.CSSProperties;
}

export const ProgressiveImage: React.FC<ProgressiveImageProps> = ({
  src,
  alt,
  className,
  placeholderClassName,
  placeholderColor = 'rgba(0, 0, 0, 0.05)',
  children,
  onLoad,
  onError,
  style,
}) => {
  const [imageState, setImageState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    let isMounted = true;

    const loadImage = async () => {
      try {
        // Dynamically import to avoid circular dependency
        const { imageCache } = await import('../utils/imageCache');

        // Get from cache or fetch
        const cachedUrl = await imageCache.get(src);

        if (!isMounted) return;

        if (cachedUrl) {
          setImageUrl(cachedUrl);
          setImageState('loaded');
          onLoad?.();
          return;
        }

        // If not in cache, load directly
        const img = new Image();
        img.src = src;

        img.onload = () => {
          if (isMounted) {
            setImageUrl(src);
            setImageState('loaded');
            onLoad?.();
          }
        };

        img.onerror = () => {
          if (isMounted) {
            setImageState('error');
            onError?.();
          }
        };
      } catch (error) {
        console.error('Failed to load image:', error);
        if (isMounted) {
          setImageState('error');
          onError?.();
        }
      }
    };

    loadImage();

    return () => {
      isMounted = false;
    };
  }, [src, onLoad, onError]);

  // Show error fallback if provided
  if (imageState === 'error' && children) {
    return <>{children}</>;
  }

  // Show placeholder while loading (before image starts loading)
  if (imageState === 'loading' && !imageUrl) {
    return (
      <div
        className={cn(placeholderClassName, className)}
        style={{ backgroundColor: placeholderColor, ...style }}
        aria-label={alt}
        role='img'
      />
    );
  }

  // Show image - no transition, just direct display
  return (
    <img
      ref={imgRef}
      src={imageUrl || src}
      alt={alt}
      className={cn('w-full h-full object-cover', className)}
      style={style}
      loading='lazy'
    />
  );
};

/**
 * Progressive Cover Image Component for Knowledge Base cards
 * Provides seamless integration with Picasso backgrounds
 */
interface ProgressiveCoverImageProps {
  src: string;
  alt: string;
  picassoClass?: string;
  children?: React.ReactNode; // Icon fallback
  onLoad?: () => void;
  onError?: () => void;
}

export const ProgressiveCoverImage: React.FC<ProgressiveCoverImageProps> = ({
  src,
  alt,
  picassoClass,
  children,
  onLoad,
  onError,
}) => {
  const [showImage, setShowImage] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleLoad = () => {
    setShowImage(true);
    onLoad?.();
  };

  const handleError = () => {
    setHasError(true);
    setShowImage(false);
    onError?.();
  };

  return (
    <div className={cn('relative w-full h-full overflow-hidden', picassoClass)}>
      {/* Picasso background with art layers - always shown as base */}
      <div className='picasso-art-layer' />
      <div className='picasso-art-fragment-1' />
      <div className='picasso-art-fragment-2' />
      <div className='absolute inset-0 flex items-center justify-center'>{children}</div>

      {/* Progressive image - overlays Picasso background when loaded */}
      {!hasError && (
        <ProgressiveImage
          src={src}
          alt={alt}
          className={showImage ? 'opacity-100' : 'opacity-0'}
          placeholderClassName='absolute inset-0'
          onLoad={handleLoad}
          onError={handleError}
        />
      )}
    </div>
  );
};
